import { Box, IconButton, Typography, Snackbar } from "@mui/material";
import { useContext, useEffect, useRef, useState, ReactNode, useMemo, useCallback } from "react";
import { WidgetContext } from "src/contexts/WidgetContext";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import SourceStories from "../SourceStories/SourceStories";
import ThoughtItem from "../ThoughtItem/ToughtItem";
import { useHandleShare } from "src/utils/handleShare";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { chatBot, getChatHistory } from "src/api/chatBot";
import { GroundingSource } from "./ChatSourcesList";
import ChatMessagesViewport from "./ChatMessagesViewport";
import ChatBotQuestionInput, { ChatBotQuestionInputHandle } from "./ChatBotQuestionInput";
import { CHATBOT_STOPPED_MESSAGE, ChatTurn } from "./chatBotTypes";
import { CHAT_HISTORY_PAGE_SIZE, mapRawHistoryToTurns } from "./chatHistoryUtils";
import { findRecoveredTurn, mergeRecoveredHistory } from "./mapChatHistoryToTurns";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import testAudio from "../../assets/test_audio_input_2.wav";
import { convertWebMToWav } from "src/utils/convertAudioToWav";
import { PcmChunkPlayer } from "src/utils/pcmChunkPlayer";
import { STREAMING_AUDIO_SAMPLE_RATE, DEFAULT_AUDIO_CHANNELS } from "src/constants/audioConfig";
import { resolveUnsyncedPocIntegrationName } from "src/constants/unsyncedLeague";
import { audioService } from "src/services/audioService";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { redirectToAuth0NoCredits } from "src/utils/auth0NoCredits";
import { ChatBubble } from "./ChatBubble";
import {
  encodeWavFromFloat32,
  stopMicCaptureKeepSession,
  sendCurrentVadSegmentCore,
  stopWebAudioRecordingCore,
  startHandsFreeWebAudioSessionCore,
} from "src/utils/chatbotFunctions";
import { isPlatform } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import SourceIcon from "../SourceStories/SourceIcon";
import CreditsUi from "../CreditsUi/CreditsUi";
import FreeCreditsUi from "../CreditsUi/FreeCreditsUi";
import { frontEnvNumber } from "src/services/runtimeFrontConfig";
import { trackChatbotMessageSent } from "src/utils/analyticsCapFirebase";

// Max characters for chatbot text input (same behavior as LeagueSettings notes field)
const CHATBOT_INPUT_MAX_LENGTH = 5000;
const RECOVERY_POLL_MS = 2500;
const RECOVERY_TIMEOUT_MS = 40000;

type PendingTurn = {
  userTranscript: string;
  historyTurnCount: number;
  /** messageId of the last history turn before this send — anchors recovery past duplicates. */
  lastKnownMessageId?: string;
  startedAt: number;
};

type ChatBotResponseStreamProps = {
  onClose: () => void;
  autoStartRecording?: boolean;
  isDrawerOpen?: boolean;
  updateCreditsAfterEndpoint: (featureUsed?: string) => Promise<void>;
  setIsBenchDrawerOpen: (open: boolean) => void;
  setIsPaymentOpen: (open: boolean) => void;
  setInitialPaymentView: (view: string) => void;
  userSubscription: string;
  isCreditsAnimating: boolean;
  creditsBoxRef: React.RefObject<HTMLDivElement>;
};

const ChatBotResponseStream: React.FC<ChatBotResponseStreamProps> = ({ onClose, autoStartRecording = false, isDrawerOpen = true, updateCreditsAfterEndpoint, setIsBenchDrawerOpen, setIsPaymentOpen, setInitialPaymentView, userSubscription, isCreditsAnimating, creditsBoxRef }) => {
  const { selectedPartner, selectedTeamInfo, userDataCache, yahooToken, leagueIdPlatform, sectionData, NON_LOGGED_IN_CONFIG, credits, freeCredits, pocToken, isLoggedInAuth0ExperienceGlobal } = useContext(WidgetContext);
  const { user, loginWithRedirect } = useAuth0();
  const authId = user?.sub || NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_AUTH_ID;
  const { handleShare, isCheckmark } = useHandleShare();
  const chatbotShareContext = useMemo(() => {
    const commonLeagueData: any = sectionData?.commonLeagueData || {};
    const platformFromCommon = (commonLeagueData?.platform_id || commonLeagueData?.platform || "").toString().trim().toLowerCase();
    const leagueFromCommon = (
      commonLeagueData?.league_info?.league_id ||
      commonLeagueData?.leagueId ||
      leagueIdPlatform ||
      selectedTeamInfo?.leagueId ||
      ""
    ).toString().trim();
    // POC get_share_link_by_request needs "un_synced" (same as live_chat / common / super).
    const integrationFromCache = selectedTeamInfo?.platformId
      ? userDataCache?.[selectedTeamInfo.platformId]?.integration_name
      : null;
    const platformIdForShare =
      resolveUnsyncedPocIntegrationName(platformFromCommon || integrationFromCache, selectedTeamInfo?.platformId) ||
      platformFromCommon ||
      undefined;
    return {
      platformId: platformIdForShare || undefined,
      leagueId: leagueFromCommon || undefined,
    };
  }, [sectionData?.commonLeagueData, leagueIdPlatform, selectedTeamInfo?.leagueId, selectedTeamInfo?.platformId, userDataCache]);
  const handleShareMessage = useCallback(
    (messageId: string) => {
      handleShare(
        messageId,
        "Check out this chatbot response!",
        undefined,
        undefined,
        chatbotShareContext.platformId,
        chatbotShareContext.leagueId,
        authId,
      );
    },
    [handleShare, chatbotShareContext.platformId, chatbotShareContext.leagueId, authId],
  );
  const containerRef = useRef(null);
  const isNative = Capacitor.isNativePlatform(); // isNative is for capacitor (maybe more accurate)
  const iosOrAndroid = isPlatform("hybrid"); // isPlatform("hybrid") is for ionic (its possible for ipad its fails)

  // isRunningNatively is for capacitor and ionic (even more accurate)
  const isRunningNatively = isNative || iosOrAndroid;
  // console.log("isNative from Capacitor/core", isNative);
  // console.log("iosOrAndroid from Ionic/react", iosOrAndroid);
  // console.log("isRunningNatively", isRunningNatively);

  ////------- AUDIO LOGIC -------////

  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();

  // Hardcoded data for development/styling purposes
  const MOCK_CHAT_HISTORY = [
    "Basándome en tu roster actual, **Christian McCaffrey** es tu mejor jugador. Está teniendo una temporada excepcional con promedios consistentes y es el RB #1 en la mayoría de las ligas. Su versatilidad tanto corriendo como recibiendo lo hace extremadamente valioso.",
    "Sí, definitivamente deberías considerar hacer ese trade. Estás recibiendo un jugador de mayor valor a largo plazo y además obtienes un pick adicional que te da más flexibilidad. El jugador que estás dando tiene un techo más bajo y este trade mejora tu roster significativamente.",
  ];

  const MOCK_USER_QUESTIONS = ["¿Cuál es mi mejor jugador?", "¿Debería hacer un trade?"];

  // const [chatHistory, setChatHistory] = useState<string[]>(MOCK_CHAT_HISTORY);
  const [chatHistory, setChatHistory] = useState<ChatTurn[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState<boolean>(false);
  const [hasMoreHistory, setHasMoreHistory] = useState<boolean>(false);
  const [oldestMessageId, setOldestMessageId] = useState<string | undefined>(undefined);
  const [currentChatTranscript, setCurrentChatTranscript] = useState<string>("");
  const [currentUserTranscript, setCurrentUserTranscript] = useState<string>("");
  const [isRecording, setIsRecording] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [isChatSessionActive, setIsChatSessionActive] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [recordedWavUrl, setRecordedWavUrl] = useState<string | null>(null);
  const [audioLogs, setAudioLogs] = useState<string[]>([]);
  const [hasLiveChatAudio, setHasLiveChatAudio] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [currentThoughtTitle, setCurrentThoughtTitle] = useState<string>("");
  /** Follow-ups queued while a turn is in flight (Cursor-style FIFO). */
  const [queuedMessages, setQueuedMessages] = useState<string[]>([]);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  // When true, show tooltip above mic; cleared when user clicks mic again to retry
  const [micPermissionDeniedHint, setMicPermissionDeniedHint] = useState(false);
  // Snackbar (Gemini-style): show for 2s when mic permission denied, with dismiss button
  const [micDeniedSnackbarOpen, setMicDeniedSnackbarOpen] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const recordedWavBlobRef = useRef<Blob | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const webAudioPcmDataRef = useRef<Float32Array[]>([]);
  const webAudioSampleRateRef = useRef<number>(44100);
  const webAudioStatsRef = useRef<{ processCount: number; nonZeroSampleCount: number; maxSampleSeen: number }>({
    processCount: 0,
    nonZeroSampleCount: 0,
    maxSampleSeen: 0,
  });

  const vadSpeechActiveRef = useRef(false);
  const vadSilenceMsRef = useRef(0);
  const vadSegmentMsRef = useRef(0);
  const isSendingSegmentRef = useRef(false);
  const currentSpeakingForAuraRef = useRef(false);

  const chatPcmPlayerRef = useRef<PcmChunkPlayer | null>(null);

  const chatSocketRef = useRef<WebSocket | null>(null);
  const [isChatSocketReady, setIsChatSocketReady] = useState(false);
  // True when we intentionally close the socket (drawer closed); prevents auto-reconnect.
  const intentionalCloseRef = useRef(false);
  // Bump to trigger reconnect after unexpected WS close (e.g. GCP Speech 5-min timeout).
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  // Ref to accumulate current turn's user transcript so we read correct value on "complete" (avoids React state timing)
  const currentUserTranscriptRef = useRef<string>("");
  // Accumulates grounding sources for the current turn (reset on each new response)
  const currentTurnSourcesRef = useRef<GroundingSource[]>([]);

  const isChatSessionActiveRef = useRef(false);
  const hasAutoStartedRef = useRef(false);
  // When true, frontend ignores incoming response chunks (user clicked stop)
  const cancelledResponseRef = useRef(false);
  const questionInputRef = useRef<ChatBotQuestionInputHandle | null>(null);
  const [currentTurnScrollTrigger, setCurrentTurnScrollTrigger] = useState(0);
  const isMicEnabledRef = useRef(false);
  const historyRequestContextRef = useRef<string>("");
  const isLoadingMoreHistoryRef = useRef(false);
  const chatHistoryRef = useRef<ChatTurn[]>([]);
  const isWaitingForResponseRef = useRef(false);
  const queuedMessagesRef = useRef<string[]>([]);
  const sendTextToBackendAndStreamRef = useRef<(text: string) => Promise<void>>(async () => {});
  const pendingTurnRef = useRef<PendingTurn | null>(null);
  const recoveryPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startHistoryRecoveryRef = useRef<() => void>(() => {});
  const stopHistoryRecoveryRef = useRef<() => void>(() => {});

  // GCS paths use integration_name (e.g. "fleaflicker"), not the frontend platform id ("Flea_flicker").
  const chatHistoryPlatform =
    (selectedTeamInfo?.platformId && userDataCache?.[selectedTeamInfo.platformId]?.integration_name) ||
    selectedTeamInfo?.platformId ||
    null;

  const resetHistoryPaginationState = useCallback(() => {
    setHasMoreHistory(false);
    setOldestMessageId(undefined);
    isLoadingMoreHistoryRef.current = false;
    setIsLoadingMoreHistory(false);
  }, []);

  const loadMoreHistory = useCallback(async () => {
    if (
      !isDrawerOpen ||
      !leagueIdPlatform ||
      !authId ||
      !chatHistoryPlatform ||
      !hasMoreHistory ||
      isLoadingHistory ||
      isLoadingMoreHistoryRef.current ||
      !oldestMessageId
    ) {
      return;
    }

    const requestContext = `${chatHistoryPlatform}:${leagueIdPlatform}:${authId}`;
    isLoadingMoreHistoryRef.current = true;
    setIsLoadingMoreHistory(true);

    try {
      const token = await getSafeAccessAuth0Token();
      const data = await getChatHistory(token, chatHistoryPlatform, leagueIdPlatform, authId, {
        limit: CHAT_HISTORY_PAGE_SIZE,
        before: oldestMessageId,
      });

      if (historyRequestContextRef.current !== requestContext) {
        return;
      }

      if (data?.history && Array.isArray(data.history) && data.history.length > 0) {
        const turns = mapRawHistoryToTurns(data.history);
        setChatHistory((prev) => [...turns, ...prev]);
      }

      setHasMoreHistory(Boolean(data?.hasMore));
      setOldestMessageId(data?.oldestId);
    } catch (error) {
      console.error("Error loading more chat history:", error);
    } finally {
      isLoadingMoreHistoryRef.current = false;
      setIsLoadingMoreHistory(false);
    }
  }, [
    authId,
    chatHistoryPlatform,
    getSafeAccessAuth0Token,
    hasMoreHistory,
    isDrawerOpen,
    isLoadingHistory,
    leagueIdPlatform,
    oldestMessageId,
  ]);

  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  useEffect(() => {
    isWaitingForResponseRef.current = isWaitingForResponse;
  }, [isWaitingForResponse]);

  useEffect(() => {
    queuedMessagesRef.current = queuedMessages;
  }, [queuedMessages]);

  const dequeueNextQueued = (): string | null => {
    const queue = queuedMessagesRef.current;
    if (queue.length === 0) return null;
    const [next, ...rest] = queue;
    queuedMessagesRef.current = rest;
    setQueuedMessages(rest);
    return next;
  };

  const sendDequeuedFollowUp = (next: string) => {
    lastSentTextRef.current = next;
    lastSendTimeRef.current = Date.now();
    hasSentSinceMicStartRef.current = true;
    questionInputRef.current?.clear();
    stableTextRef.current = "";
    void sendTextToBackendAndStreamRef.current(next);
  };

  const stopHistoryRecovery = useCallback(() => {
    if (recoveryPollRef.current) {
      clearInterval(recoveryPollRef.current);
      recoveryPollRef.current = null;
    }
  }, []);

  const completeHistoryRecovery = useCallback(
    (freshPageTurns: ChatTurn[]) => {
      stopHistoryRecovery();
      pendingTurnRef.current = null;
      setChatHistory((prev) => mergeRecoveredHistory(prev, freshPageTurns));
      setIsWaitingForResponse(false);
      setCurrentChatTranscript("");
      setCurrentUserTranscript("");
      setCurrentThoughtTitle("");
      currentUserTranscriptRef.current = "";
      currentTurnSourcesRef.current = [];
      void updateCreditsAfterEndpoint("chatbot");
    },
    [stopHistoryRecovery, updateCreditsAfterEndpoint],
  );

  const startHistoryRecovery = useCallback(async () => {
    if (recoveryPollRef.current) return;
    if (!pendingTurnRef.current || cancelledResponseRef.current) return;
    if (!isWaitingForResponseRef.current) return;
    if (!leagueIdPlatform || !authId || !chatHistoryPlatform) return;

    const pollOnce = async () => {
      const pending = pendingTurnRef.current;
      if (!pending || cancelledResponseRef.current || !isWaitingForResponseRef.current) {
        stopHistoryRecovery();
        return;
      }

      if (Date.now() - pending.startedAt > RECOVERY_TIMEOUT_MS) {
        console.warn("[Chatbot WS] History recovery timed out");
        stopHistoryRecovery();
        pendingTurnRef.current = null;
        setIsWaitingForResponse(false);
        setCurrentChatTranscript("");
        setCurrentUserTranscript("");
        setCurrentThoughtTitle("");
        currentUserTranscriptRef.current = "";
        return;
      }

      try {
        const token = await getSafeAccessAuth0Token();
        const data = await getChatHistory(token, chatHistoryPlatform, leagueIdPlatform, authId, {
          limit: CHAT_HISTORY_PAGE_SIZE,
        });
        if (!data?.history || !Array.isArray(data.history)) return;

        const turns = mapRawHistoryToTurns(data.history);
        if (
          findRecoveredTurn(
            turns,
            pending.userTranscript,
            pending.historyTurnCount,
            pending.lastKnownMessageId,
          )
        ) {
          // Only refresh pagination cursors when we are not keeping an older loaded prefix
          const prev = chatHistoryRef.current;
          const firstFreshId = turns[0]?.messageId;
          const overlapStart = firstFreshId
            ? prev.findIndex((turn) => turn.messageId === firstFreshId)
            : -1;
          const keepsOlderPrefix = overlapStart > 0;
          if (!keepsOlderPrefix) {
            setHasMoreHistory(Boolean(data.hasMore));
            setOldestMessageId(data.oldestId);
          }
          completeHistoryRecovery(turns);
        }
      } catch (error) {
        console.warn("[Chatbot WS] History recovery poll failed", error);
      }
    };

    void pollOnce();
    recoveryPollRef.current = setInterval(() => {
      void pollOnce();
    }, RECOVERY_POLL_MS);
  }, [
    authId,
    chatHistoryPlatform,
    leagueIdPlatform,
    getSafeAccessAuth0Token,
    stopHistoryRecovery,
    completeHistoryRecovery,
  ]);

  const registerPendingTurn = useCallback((userTranscript: string) => {
    const previousTurns = chatHistoryRef.current;
    pendingTurnRef.current = {
      userTranscript,
      historyTurnCount: previousTurns.length,
      lastKnownMessageId: previousTurns[previousTurns.length - 1]?.messageId,
      startedAt: Date.now(),
    };
  }, []);

  useEffect(() => {
    startHistoryRecoveryRef.current = () => {
      void startHistoryRecovery();
    };
    stopHistoryRecoveryRef.current = stopHistoryRecovery;
  }, [startHistoryRecovery, stopHistoryRecovery]);

  // Fetch chat history for the current league and user
  useEffect(() => {
    const fetchHistory = async () => {
      if (!isDrawerOpen || !leagueIdPlatform || !authId || !chatHistoryPlatform) return;

      const requestContext = `${chatHistoryPlatform}:${leagueIdPlatform}:${authId}`;
      historyRequestContextRef.current = requestContext;

      setIsLoadingHistory(true);
      setChatHistory([]); // Clear history before fetching new one for this league
      resetHistoryPaginationState();

      try {
        const token = await getSafeAccessAuth0Token();
        const data = await getChatHistory(token, chatHistoryPlatform, leagueIdPlatform, authId, {
          limit: CHAT_HISTORY_PAGE_SIZE,
        });

        if (historyRequestContextRef.current !== requestContext) {
          return;
        }

        if (data?.history && Array.isArray(data.history)) {
          setChatHistory(mapRawHistoryToTurns(data.history));
          setHasMoreHistory(Boolean(data.hasMore));
          setOldestMessageId(data.oldestId);
        }
      } catch (error) {
        console.error("Error fetching chat history:", error);
      } finally {
        if (historyRequestContextRef.current === requestContext) {
          setIsLoadingHistory(false);
        }
      }
    };

    fetchHistory();
  }, [
    isDrawerOpen,
    leagueIdPlatform,
    authId,
    chatHistoryPlatform,
    getSafeAccessAuth0Token,
    resetHistoryPaginationState,
  ]);

  // ===== WEB SPEECH API (OPTION A) - COMMENTED OUT =====
  // This approach uses browser's SpeechRecognition for live transcription.
  // It worked on desktop Chrome but failed on iOS/Capacitor (testestest issue).
  // TO RE-ENABLE: Uncomment the refs below, the useEffect at line ~215, and the onChange reset logic at line ~1446.
  // const speechRecognitionRef = useRef<{ stop: () => void; abort: () => void } | null>(null);
  // const initialTextInputForSpeechRef = useRef<string>("");
  // const speechFinalRef = useRef<string>("");
  // const speechInterimRef = useRef<string>("");
  // const textInputRef = useRef(textInput);
  // useEffect(() => { textInputRef.current = textInput; }, [textInput]);
  // ===== END WEB SPEECH API =====

  // GCP Speech-to-Text streaming logic
  // "stableTextRef" acts as the source of truth for "committed" text (manually typed OR finalized speech).
  // Interim speech is temporarily appended to this for display, but doesn't modify it until finalized.
  const stableTextRef = useRef<string>("");

  // Ignore live_transcript_chunk for this many ms after send (trailing chunks from old stream after we restart)
  const GCP_IGNORE_MS_AFTER_SEND = 1200;
  const lastSendTimeRef = useRef<number>(0);
  const lastSentTextRef = useRef<string>(""); // avoid repopulating field with the message we just sent
  const hasSentSinceMicStartRef = useRef<boolean>(false);
  // Flag to discard trailing transcripts if user interrupts by typing
  const ignorePendingTranscriptsRef = useRef<boolean>(false);

  // Interval for sending audio chunks to GCP (approx 100ms)
  const transcriptionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Params used when mic was opened (so reconnect uses same config as "open mic from 0")
  const lastTranscriptionParamsRef = useRef<{ sampleRateHertz: number; languageCode: string } | null>(null);

  useEffect(() => {
    isChatSessionActiveRef.current = isChatSessionActive;
  }, [isChatSessionActive]);

  useEffect(() => {
    isMicEnabledRef.current = isMicEnabled;
  }, [isMicEnabled]);

  // Auto-start recording once when component opens if requested by parent, but only after WS is ready and mic is enabled
  useEffect(() => {
    if (autoStartRecording && !hasAutoStartedRef.current && !isRecording && isChatSocketReady && isMicEnabled) {
      hasAutoStartedRef.current = true;
      void handlePlayAudio();
    }
  }, [autoStartRecording, isRecording, isChatSocketReady, isMicEnabled]);

  // Monitor VAD state and pause/resume bot audio when user speaks
  useEffect(() => {
    if (!isChatSessionActive || !audioContextRef.current) {
      return;
    }

    const checkVadState = () => {
      if (chatPcmPlayerRef.current && typeof chatPcmPlayerRef.current.pause === "function" && typeof chatPcmPlayerRef.current.resume === "function") {
        if (vadSpeechActiveRef.current) {
          // User is speaking - pause bot audio
          chatPcmPlayerRef.current.pause();
        } else {
          // User stopped speaking - resume bot audio
          chatPcmPlayerRef.current.resume();
        }
      }
    };

    // Check VAD state every 100ms
    const intervalId = setInterval(checkVadState, 100);

    return () => {
      clearInterval(intervalId);
      // Ensure audio is resumed when component unmounts or session stops
      if (chatPcmPlayerRef.current && typeof chatPcmPlayerRef.current.resume === "function") {
        chatPcmPlayerRef.current.resume();
      }
    };
  }, [isChatSessionActive]);

  // Poll aura-speaking ref so React re-renders; aura expands when user speaks, retracts when silent
  useEffect(() => {
    if (!isRecording) {
      setIsUserSpeaking(false);
      return;
    }
    let lastValue = currentSpeakingForAuraRef.current;
    const intervalId = setInterval(() => {
      const next = currentSpeakingForAuraRef.current;
      if (next !== lastValue) {
        lastValue = next;
        setIsUserSpeaking(next);
      }
    }, 80);
    return () => {
      clearInterval(intervalId);
      setIsUserSpeaking(false);
    };
  }, [isRecording]);

  // ===== WEB SPEECH API useEffect - COMMENTED OUT =====
  // TO RE-ENABLE: Uncomment this entire useEffect block.
  // useEffect(() => {
  //   const win = typeof window !== "undefined" ? window : null;
  //   const SpeechRecognitionCtor =
  //     win && ((win as any).SpeechRecognition || (win as any).webkitSpeechRecognition);
  //
  //   if (!isRecording) {
  //     const rec = speechRecognitionRef.current;
  //     if (rec) {
  //       try {
  //         rec.abort();
  //       } catch {
  //         try {
  //           (rec as any).stop?.();
  //         } catch {
  //           // ignore
  //         }
  //       }
  //       speechRecognitionRef.current = null;
  //     }
  //     return;
  //   }
  //
  //   if (!SpeechRecognitionCtor) {
  //     return;
  //   }
  //
  //   initialTextInputForSpeechRef.current = textInputRef.current;
  //   speechFinalRef.current = "";
  //   speechInterimRef.current = "";
  //
  //   const recognition = new SpeechRecognitionCtor() as any;
  //   recognition.continuous = true;
  //   recognition.interimResults = true;
  //   recognition.lang = ""; // browser default / user language
  //
  //   recognition.onresult = (event: any) => {
  //     const initial = initialTextInputForSpeechRef.current;
  //     let finalPart = speechFinalRef.current;
  //     let interimPart = "";
  //     for (let i = event.resultIndex ?? 0; i < (event.results?.length ?? 0); i++) {
  //       const r = event.results[i];
  //       const transcript = (r?.[0]?.transcript ?? "") as string;
  //       if (r?.isFinal) {
  //         finalPart += transcript;
  //         interimPart = "";
  //       } else {
  //         interimPart = transcript;
  //       }
  //     }
  //     speechFinalRef.current = finalPart;
  //     speechInterimRef.current = interimPart;
  //     setTextInput(initial + finalPart + interimPart);
  //   };
  //
  //   recognition.onerror = (event: any) => {
  //     if (event?.error !== "aborted" && event?.error !== "no-speech") {
  //       console.warn("[Chatbot] Web Speech API error:", event?.error);
  //     }
  //   };
  //
  //   try {
  //     recognition.start();
  //     speechRecognitionRef.current = recognition;
  //   } catch (e) {
  //     console.warn("[Chatbot] Web Speech API start failed:", e);
  //   }
  //
  //   return () => {
  //     try {
  //       if (speechRecognitionRef.current === recognition) {
  //         recognition.abort?.();
  //       }
  //     } catch {
  //       // ignore
  //     }
  //     if (speechRecognitionRef.current === recognition) {
  //       speechRecognitionRef.current = null;
  //     }
  //   };
  // }, [isRecording]);
  // ===== END WEB SPEECH API useEffect =====

  // Stop user voice recording (helper function)
  const handleStopMic = () => {
    // Check ref to avoid duplicate calls
    if (!isMicEnabledRef.current) return;

    stopMicCaptureKeepSession(mediaRecorderRef, mediaStreamRef as React.MutableRefObject<MediaStream | null>, addLog, setIsRecording);
    // Stop Web Audio path and send stop_transcription so backend closes GCP stream
    void stopWebAudioRecording();

    // iOS Safari/Chrome mobile: stopping getUserMedia can suspend the playback AudioContext.
    // Try to resume the PcmChunkPlayer's AudioContext so audio playback doesn't stop.
    if (isMobileWebBrowser() && chatPcmPlayerRef.current && typeof chatPcmPlayerRef.current.resumeIfSuspended === "function") {
      void chatPcmPlayerRef.current.resumeIfSuspended();
    }

    setIsMicEnabled(false);
    isMicEnabledRef.current = false; // Update immediately to block subsequent calls
  };

  // function to toggle the microphone (only affects user mic, not assistant playback)
  // First click: request permission and start session (handlePlayAudio). Second click: stop recording and disable mic.
  const handleToggleMic = () => {
    if (!isMicEnabled) {
      // Clear any previous "permission denied" hint when user explicitly retries
      setMicPermissionDeniedHint(false);
      // User clicks mic icon to enable: start session (requests permission via getUserMedia inside handlePlayAudio)
      handleStartSessionClick();
      return;
    }

    // User clicks mic icon to disable
    handleStopMic();
  };

  // Exact same mic-release logic as handleToggleMic when disabling mic, then close drawer. Used on X/back so browser drops mic.
  const handleDrawerClose = () => {
    stopMicCaptureKeepSession(mediaRecorderRef, mediaStreamRef as React.MutableRefObject<MediaStream | null>, addLog, setIsRecording);
    void stopWebAudioRecording();
    if (isMobileWebBrowser() && chatPcmPlayerRef.current && typeof chatPcmPlayerRef.current.resumeIfSuspended === "function") {
      void chatPcmPlayerRef.current.resumeIfSuspended();
    }
    setIsMicEnabled(false);
    setIsChatSessionActive(false);
    isChatSessionActiveRef.current = false;
    setIsRecording(false);
    onClose();
  };

  const handleStartSessionClick = () => {
    setIsChatSessionActive(true);
    isChatSessionActiveRef.current = true;
    // Pass true to bypass isMicEnabled check (user just clicked mic to enable)
    void handlePlayAudio(true);
  };

  const handleStopSessionClick = () => {
    handleStopAudio();
  };

  const chatBotCreditsCost = frontEnvNumber("REACT_APP_CHATBOT_CREDITS", 10);

  // Gate: open bench/paywall when balance is below chatbot cost (REACT_APP_CHATBOT_CREDITS).
  // Free subscription → check freeCredits; Paid subscription → check credits (sub+booster).
  // Do not call onClose() here: that runs handlePartnerSelectAndCloseDrawer and tears down the partner/chatbot drawer.
  const checkCreditGate = (): boolean => {
    const subscriptionIsFree = (userSubscription || "free").toLowerCase() === "free";
    if (subscriptionIsFree && freeCredits >= chatBotCreditsCost) return false;
    if (!subscriptionIsFree && (credits === null || credits >= chatBotCreditsCost)) return false;
    if (subscriptionIsFree) {
      // Non-logged out of credits: redirect to Auth0 (no-credits message) instead of BenchDrawer.
      // checkCreditGate is sync; the redirect navigates away, so fire-and-forget is fine.
      if (!isLoggedInAuth0ExperienceGlobal) {
        void redirectToAuth0NoCredits(loginWithRedirect);
      } else {
        setInitialPaymentView("manageMembership");
        setIsPaymentOpen(true);
      }
    } else {
      setInitialPaymentView("manageMembership");
      setIsPaymentOpen(true);
    }
    return true;
  };

  const handleSendText = (trimmed: string) => {
    if (!trimmed) {
      return;
    }
    if (checkCreditGate()) return;
    if (!isChatSocketReady || !chatSocketRef.current) {
      addLog("Chatbot WebSocket not ready; text send is ignored.", "warn");
      return;
    }
    if (isMicEnabledRef.current) {
      handleStopMic();
    }

    // While a turn is in flight, append to the follow-up queue.
    if (isWaitingForResponse || currentChatTranscript) {
      setQueuedMessages((prev) => [...prev, trimmed]);
      stableTextRef.current = "";
      if (chatSocketRef.current?.readyState === WebSocket.OPEN) {
        chatSocketRef.current.send(JSON.stringify({ type: "stop_transcription" }));
      }
      return;
    }

    void sendTextToBackendAndStream(trimmed);
    stableTextRef.current = "";
    lastSentTextRef.current = trimmed;
    lastSendTimeRef.current = Date.now();
    hasSentSinceMicStartRef.current = true;

    if (chatSocketRef.current?.readyState === WebSocket.OPEN) {
      chatSocketRef.current.send(JSON.stringify({ type: "stop_transcription" }));
    }
  };

  const handleRemoveQueuedAt = (index: number) => {
    setQueuedMessages((prev) => prev.filter((_, i) => i !== index));
  };

  // Cancel the in-flight response: tell backend to close POC session, commit partial turn to history, restore user question
  const handleCancelResponse = () => {
    cancelledResponseRef.current = true;
    stopHistoryRecovery();
    pendingTurnRef.current = null;

    // Tell backend to close POC WS for this turn (stops chunk generation at the source)
    if (chatSocketRef.current?.readyState === WebSocket.OPEN) {
      chatSocketRef.current.send(JSON.stringify({ type: "cancel_response" }));
    }

    const questionToRestore = currentUserTranscriptRef.current;
    const partialAssistant = currentChatTranscript;
    // When user stopped during "thinking" (no response text yet), show placeholder like Gemini
    const assistantText = partialAssistant.trim() ? partialAssistant : CHATBOT_STOPPED_MESSAGE;

    // Commit the partial turn to chatHistory so it stays visible in the UI
    if (questionToRestore || assistantText) {
      setChatHistory((prev) => [
        ...prev,
        {
          userTranscript: questionToRestore,
          assistantTranscript: assistantText,
        },
      ]);
    }

    // Clear "in-progress" state (the turn now lives in chatHistory)
    setIsWaitingForResponse(false);
    setCurrentChatTranscript("");
    setCurrentUserTranscript("");
    setCurrentThoughtTitle("");
    currentUserTranscriptRef.current = "";

    // Stop any active audio playback for this turn
    if (chatPcmPlayerRef.current) {
      chatPcmPlayerRef.current.stop();
      chatPcmPlayerRef.current = null;
    }
    setHasLiveChatAudio(false);

    // Turn is done locally; send next queued follow-up (Stop does not clear the queue).
    const nextQueued = dequeueNextQueued();
    if (nextQueued) {
      sendDequeuedFollowUp(nextQueued);
    } else if (questionToRestore) {
      questionInputRef.current?.setValue(questionToRestore);
      stableTextRef.current = questionToRestore;
    }
  };

  // Clear chat history when component unmounts (drawer is closed)
  useEffect(() => {
    return () => {
      setChatHistory([]);
      resetHistoryPaginationState();
      setCurrentChatTranscript("");
      setCurrentUserTranscript("");
      setQueuedMessages([]);
    };
  }, [resetHistoryPaginationState]);

  // Initialize persistent WebSocket connection front-back for chatbot (only when drawer is open).
  // Also re-runs when reconnectTrigger bumps (auto-reconnect after unexpected close).
  useEffect(() => {
    if (!isDrawerOpen) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      console.warn("REACT_APP_BACKEND_URL is not defined; chatbot WS will not be used.");
      return;
    }

    const wsUrl = backendUrl.replace(/^http/, "ws") + "/chatbot/live";
    intentionalCloseRef.current = false;

    try {
      const socket = new WebSocket(wsUrl); // WebSocket connection OPEN to the backend for chatbot.
      chatSocketRef.current = socket;

      socket.onopen = () => {
        console.log("✅ [Chatbot WS] Connection opened to backend");
        setIsChatSocketReady(true);

        // After reconnect, re-open transcription if mic was left on; use same params as when mic was opened.
        if (isMicEnabledRef.current) {
          const params = lastTranscriptionParamsRef.current ?? {
            sampleRateHertz: audioContextRef.current?.sampleRate ?? 48000,
            languageCode: (() => {
              const browserLang = (typeof navigator !== "undefined" && navigator.language) ? navigator.language.toLowerCase() : "en";
              return browserLang.startsWith("es") ? "es-419" : browserLang.startsWith("pt") ? "pt-BR" : browserLang.startsWith("fr") ? "fr-FR" : "en-US";
            })(),
          };
          socket.send(JSON.stringify({ type: "start_transcription", sampleRateHertz: params.sampleRateHertz, languageCode: params.languageCode }));
          console.log("[Chatbot WS] Re-opened transcription after reconnect (mic still on)", params.languageCode);
        }

        if (pendingTurnRef.current && isWaitingForResponseRef.current && !cancelledResponseRef.current) {
          startHistoryRecoveryRef.current();
        }
      };

      socket.onclose = () => {
        chatSocketRef.current = null;
        if (intentionalCloseRef.current) {
          console.log("❌ [Chatbot WS] Connection closed (intentional)");
          setIsChatSocketReady(false);
        } else {
          // Unexpected close (GCP timeout, network, etc.) — reconnect silently without disabling UI.
          console.log("🔄 [Chatbot WS] Connection lost, reconnecting silently…");
          if (isWaitingForResponseRef.current && pendingTurnRef.current && !cancelledResponseRef.current) {
            startHistoryRecoveryRef.current();
          }
          setReconnectTrigger((prev) => prev + 1);
        }
      };

      socket.onerror = (event) => {
        console.warn("Chatbot websocket error", event);
      };

      socket.onmessage = async (event) => {
        try {
          const msg = JSON.parse(event.data);
          const eventType = msg.type;
          const payload = msg.payload;

          // Skip response events when user cancelled the current turn
          if (cancelledResponseRef.current) {
            const responseEvents = ["output_transcript_chunk", "transcript_chunk", "thought", "input_transcript_chunk", "audio_chunk", "sources", "complete"];
            if (responseEvents.includes(eventType)) {
              if (eventType === "complete") {
                cancelledResponseRef.current = false;
                currentTurnSourcesRef.current = [];
              }
              return;
            }
          }

          if ((eventType === "output_transcript_chunk" || eventType === "transcript_chunk") && typeof payload === "string") {
            // First chunk of response - hide loader and clear thought
            console.log("[thought-debug] FE text_chunk clears thought", payload?.slice?.(0, 40));
            setIsWaitingForResponse(false);
            setCurrentThoughtTitle("");
            setCurrentChatTranscript((prev) => prev + payload);
          } else if (eventType === "thought") {
            // LLM thought: show titular (first line) in place of "thinking", same effect
            const raw =
              typeof payload === "string"
                ? payload
                : payload && typeof payload === "object" && (payload as any).title != null
                  ? String((payload as any).title)
                  : JSON.stringify(payload ?? "");
            const header = raw.split("\n")[0]?.trim() || "";
            console.log("[thought-debug] FE thought", { raw: String(raw).slice(0, 200), header });
            setCurrentThoughtTitle(header);
          } else if (eventType === "input_transcript_chunk" && typeof payload === "string") {
            currentUserTranscriptRef.current += payload;
            setCurrentUserTranscript((prev) => prev + payload);
          } else if (eventType === "audio_chunk" && typeof payload === "string" && payload.length > 0) {
            // First audio chunk - hide loader and clear thought
            setIsWaitingForResponse(false);
            setCurrentThoughtTitle("");
            if (!chatPcmPlayerRef.current) {
              console.log("[Chat Live Audio] First audio_chunk received (WS), creating PcmChunkPlayer");
              audioService.stopCurrentAudio();
              // Slightly faster playback for chatbot voice (playbackRate > 1.0)
              // chatPcmPlayerRef.current = new PcmChunkPlayer({ sourceSampleRate: 24000, channels: 1, label: "chatbot" });
              chatPcmPlayerRef.current = new PcmChunkPlayer({ sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE, channels: DEFAULT_AUDIO_CHANNELS, label: "chatbot", playbackRate: 1.03 });
              try {
                await chatPcmPlayerRef.current.start();
              } catch (e) {
                console.error("[Chat Live Audio] Failed to start PcmChunkPlayer (WS)", e);
              }
              setHasLiveChatAudio(true);
            }
            try {
              chatPcmPlayerRef.current?.addChunkBase64(payload, "audio/pcm");
            } catch (e) {
              console.error("[Chat Live Audio] Failed to add PCM chunk to player (WS)", e);
            }
          } else if (eventType === "sources") {
            // Accumulate grounding sources for this turn; committed to ChatTurn on "complete"
            if (Array.isArray(payload)) {
              currentTurnSourcesRef.current = payload as GroundingSource[];
            }
          } else if (eventType === "complete") {
            stopHistoryRecoveryRef.current();
            pendingTurnRef.current = null;
            chatPcmPlayerRef.current?.signalEndOfPcmStream();
            // Hide loader when turn is complete and clear thought
            setIsWaitingForResponse(false);
            setCurrentThoughtTitle("");

            // Refresh credits/freeCredits/subscription from backend (CHATBOT_CREDITS consumed per response)
            void updateCreditsAfterEndpoint("chatbot");

            const finalAssistantTranscript: string =
              typeof payload?.transcript === "string" && payload.transcript.length > 0
                ? payload.transcript
                : currentChatTranscript;
            // Use ref so we have the user transcript even if React state from input_transcript_chunk hasn't flushed yet
            const finalUserTranscript: string = currentUserTranscriptRef.current;

            const messageId = payload?.message_id;
            const cacheName = payload?.cache_name;
            const turnTimestamp: string | undefined = typeof payload?.timestamp === "string" ? payload.timestamp : undefined;
            // Prefer sources from the dedicated "sources" event; fall back to turn_end payload
            const groundingSources: GroundingSource[] =
              currentTurnSourcesRef.current.length > 0
                ? currentTurnSourcesRef.current
                : Array.isArray(payload?.grounding_sources)
                  ? (payload.grounding_sources as GroundingSource[])
                  : [];

            if (finalAssistantTranscript.length > 0 || finalUserTranscript.length > 0) {
              setChatHistory((prev) => [
                ...prev,
                {
                  userTranscript: finalUserTranscript,
                  assistantTranscript: finalAssistantTranscript,
                  messageId: messageId,
                  cacheName: cacheName,
                  groundingSources: groundingSources.length > 0 ? groundingSources : undefined,
                  timestamp: turnTimestamp,
                },
              ]);
            }

            setCurrentChatTranscript("");
            setCurrentUserTranscript("");
            currentUserTranscriptRef.current = "";
            currentTurnSourcesRef.current = [];

            // Drain next queued follow-up now that the turn finished.
            const nextQueued = dequeueNextQueued();
            if (nextQueued) {
              lastSentTextRef.current = nextQueued;
              lastSendTimeRef.current = Date.now();
              hasSentSinceMicStartRef.current = true;
              void sendTextToBackendAndStreamRef.current(nextQueued);
            }
          } else if (eventType === "live_transcript_chunk") {
            // Ignore chunks for a short window after send (stale chunks); after that accept so second question shows
            if (Date.now() - lastSendTimeRef.current < GCP_IGNORE_MS_AFTER_SEND) {
              return;
            }
            // Ignore if user stopped mic by typing (avoids appending old speech to new manual text)
            if (ignorePendingTranscriptsRef.current) {
              return;
            }
            // GCP Speech-to-Text streaming: partial transcript from backend
            const transcript = payload?.transcript || "";
            const isFinal = payload?.isFinal || false;

            // Simple robust logic:
            // 1. We have "stableText" (what user typed + previously finalized speech).
            // 2. We have "transcript" (incoming speech).
            // 3. Display = stableText + " " + transcript.

            // If it's a final result, we commit it to stableText.
            if (isFinal) {
              if (transcript.trim()) {
                const newStable = (stableTextRef.current + " " + transcript).trim();
                stableTextRef.current = newStable;
                questionInputRef.current?.setValue(newStable);
              }
            } else {
              const preview = (stableTextRef.current + " " + transcript).trim();
              questionInputRef.current?.setValue(preview);
            }
          } else if (eventType === "generation_orphaned") {
            if (!cancelledResponseRef.current && isWaitingForResponseRef.current) {
              startHistoryRecoveryRef.current();
            }
          } else if (eventType === "error") {
            // Hide loader on error and clear thought
            setIsWaitingForResponse(false);
            setCurrentThoughtTitle("");
            console.warn("Chatbot WS server error:", payload);
          }
        } catch (e) {
          console.warn("Error parsing chatbot WS message", e);
        }
      };

      return () => {
        stopHistoryRecoveryRef.current();
        intentionalCloseRef.current = true;
        try {
          socket.close();
        } catch {
          // ignore
        }
        chatSocketRef.current = null;
      };
    } catch (e) {
      console.warn("Failed to create chatbot WebSocket", e);
    }
  }, [isDrawerOpen, reconnectTrigger]);

  const chatTypewriterSpeed = iosOrAndroid ? 0 : 1;
  const hasActiveTurn = Boolean(currentChatTranscript) || isWaitingForResponse;
  const messagesViewportVisible = isLoadingHistory || chatHistory.length > 0 || hasActiveTurn;
  const chatTeamContext = useMemo(
    () => ({
      platformId: selectedTeamInfo?.platformId,
      leagueId: selectedTeamInfo?.leagueId,
      teamName: selectedTeamInfo?.teamName,
    }),
    [selectedTeamInfo?.platformId, selectedTeamInfo?.leagueId, selectedTeamInfo?.teamName],
  );

  const handleStopMicFromTyping = useCallback(() => {
    if (isMicEnabledRef.current) {
      handleStopMic();
      ignorePendingTranscriptsRef.current = true;
    }
  }, []);

  const handleStableTextChange = useCallback((text: string) => {
    stableTextRef.current = text;
  }, []);

  // Helper function to add logs to both console and UI
  const addLog = (message: string, type: "log" | "error" | "warn" = "log") => {
    const timestamp = new Date().toLocaleTimeString();
    const logMessage = `[${timestamp}] ${message}`;
    console[type](logMessage);
    setAudioLogs((prev) => [...prev, logMessage]);
  };

  // Helper to send a single VAD segment (current PCM buffer) as one question turn
  const sendCurrentVadSegment = async () => {
    await sendCurrentVadSegmentCore(
      isChatSessionActiveRef,
      webAudioPcmDataRef,
      webAudioSampleRateRef,
      webAudioStatsRef,
      vadSpeechActiveRef,
      vadSilenceMsRef,
      vadSegmentMsRef,
      isSendingSegmentRef,
      addLog,
      sendAudioToBackendAndStream
    );
  };

  const stopWebAudioRecording = async () => {
    await stopWebAudioRecordingCore(
      audioContextRef,
      scriptProcessorRef,
      sourceNodeRef,
      mediaStreamRef,
      webAudioPcmDataRef,
      webAudioStatsRef,
      vadSpeechActiveRef,
      vadSilenceMsRef,
      vadSegmentMsRef,
      recordedAudioUrl,
      recordedWavUrl,
      setRecordedAudioUrl,
      setRecordedWavUrl,
      recordedWavBlobRef,
      setIsRecording,
      addLog,
      chatSocketRef, // GCP transcription: send stop_transcription
      transcriptionIntervalRef // GCP transcription: clear interval
    );
  };

  // Start continuous hands-free recording with simple VAD to segment questions
  const startHandsFreeWebAudioSession = async () => {
    // Reset GCP transcript refs
    stableTextRef.current = "";
    lastSentTextRef.current = ""; // allow transcript to show again in new session
    // Accept transcription chunks again (user started a new mic session)
    hasSentSinceMicStartRef.current = false;
    ignorePendingTranscriptsRef.current = false;
    await startHandsFreeWebAudioSessionCore(
      recordedAudioUrl,
      recordedWavUrl,
      setRecordedAudioUrl,
      setRecordedWavUrl,
      setAudioLogs,
      mediaStreamRef,
      audioContextRef,
      webAudioSampleRateRef,
      sourceNodeRef,
      scriptProcessorRef,
      webAudioPcmDataRef,
      webAudioStatsRef,
      vadSpeechActiveRef,
      vadSilenceMsRef,
      vadSegmentMsRef,
      isChatSessionActiveRef,
      isMicEnabledRef,
      setIsRecording,
      setIsChatSessionActive,
      sendCurrentVadSegment,
      addLog,
      currentSpeakingForAuraRef,
      false, // sendOnSilence: do not auto-send on VAD silence; send only on Send/Enter
      chatSocketRef, // GCP transcription: WebSocket for sending chunks
      true, // sendTranscriptionChunks: enable GCP Speech-to-Text streaming
      transcriptionIntervalRef, // GCP transcription: interval ref for cleanup
      (params) => { lastTranscriptionParamsRef.current = params; } // store params for reconnect
    );
  };

  // // Helper to send a single VAD segment (current PCM buffer) as one question turn
  // const sendCurrentVadSegment = async () => {
  //   if (!isChatSessionActiveRef.current) {
  //     return;
  //   }

  //   const chunks = webAudioPcmDataRef.current;
  //   const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);

  //   if (totalLength === 0) {
  //     return;
  //   }

  //   if (isSendingSegmentRef.current) {
  //     return;
  //   }
  //   isSendingSegmentRef.current = true;

  //   try {
  //     const merged = new Float32Array(totalLength);
  //     let offset = 0;
  //     let maxAbsSample = 0;
  //     for (const chunk of chunks) {
  //       merged.set(chunk, offset);
  //       for (let i = 0; i < chunk.length; i++) {
  //         const v = Math.abs(chunk[i]);
  //         if (v > maxAbsSample) {
  //           maxAbsSample = v;
  //         }
  //       }
  //       offset += chunk.length;
  //     }

  //     addLog(`VAD segment: length=${merged.length}, maxAbsSample=${maxAbsSample.toFixed(4)}`);

  //     const wavBlob = encodeWavFromFloat32(merged, webAudioSampleRateRef.current || 44100);
  //     addLog(`VAD WAV blob created: size=${wavBlob.size} bytes, sampleRate=${webAudioSampleRateRef.current}, maxAbsSample=${maxAbsSample.toFixed(4)}`);

  //     // Reset buffer and stats for next question turn
  //     webAudioPcmDataRef.current = [];
  //     webAudioStatsRef.current = {
  //       processCount: 0,
  //       nonZeroSampleCount: 0,
  //       maxSampleSeen: 0,
  //     };
  //     vadSpeechActiveRef.current = false;
  //     vadSilenceMsRef.current = 0;
  //     vadSegmentMsRef.current = 0;

  //     // Send this question turn to backend (WS) and let audio/transcript stream back
  //     await sendAudioToBackendAndStream(wavBlob);
  //   } catch (err) {
  //     addLog(`Error while building/sending VAD segment: ${err instanceof Error ? err.message : String(err)}`, "error");
  //   } finally {
  //     isSendingSegmentRef.current = false;
  //   }
  // };

  // const stopWebAudioRecording = async () => {
  //   const audioContext = audioContextRef.current;
  //   const processor = scriptProcessorRef.current;
  //   const source = sourceNodeRef.current;

  //   addLog("Stopping WebAudio recorder");

  //   // Log final stats
  //   const stats = webAudioStatsRef.current;
  //   addLog(`WebAudio final stats: ${stats.processCount} buffers processed, ${stats.nonZeroSampleCount} non-zero samples, maxAbs=${stats.maxSampleSeen.toFixed(4)}`);

  //   if (source && processor) {
  //     try {
  //       source.disconnect(processor);
  //       processor.disconnect();
  //     } catch (e) {
  //       addLog(`Error disconnecting WebAudio nodes: ${e instanceof Error ? e.message : String(e)}`, "warn");
  //     }
  //   }

  //   if (audioContext && audioContext.state !== "closed") {
  //     try {
  //       await audioContext.close();
  //     } catch (e) {
  //       addLog(`Error closing AudioContext ${e instanceof Error ? e.message : String(e)}`, "warn");
  //     }
  //   }

  //   audioContextRef.current = null;
  //   scriptProcessorRef.current = null;
  //   sourceNodeRef.current = null;

  //   if (mediaStreamRef.current) {
  //     mediaStreamRef.current.getTracks().forEach((track) => {
  //       try {
  //         track.stop();
  //       } catch (e) {
  //         addLog(`Error stopping media track for WebAudio: ${e instanceof Error ? e.message : String(e)}`, "warn");
  //       }
  //     });
  //     mediaStreamRef.current = null;
  //   }

  //   // At stop we do not send anything new; VAD segments are already sent while recording
  //   webAudioPcmDataRef.current = [];
  //   webAudioStatsRef.current = {
  //     processCount: 0,
  //     nonZeroSampleCount: 0,
  //     maxSampleSeen: 0,
  //   };
  //   vadSpeechActiveRef.current = false;
  //   vadSilenceMsRef.current = 0;
  //   vadSegmentMsRef.current = 0;

  //   if (recordedAudioUrl) {
  //     URL.revokeObjectURL(recordedAudioUrl);
  //   }
  //   if (recordedWavUrl) {
  //     URL.revokeObjectURL(recordedWavUrl);
  //   }

  //   setRecordedAudioUrl(null);
  //   setRecordedWavUrl(null);
  //   recordedWavBlobRef.current = null;

  //   setIsRecording(false);
  // };

  // // Start continuous hands-free recording with simple VAD to segment questions
  // const startHandsFreeWebAudioSession = async () => {
  //   // Basic feature detection
  //   if (typeof window === "undefined" || typeof navigator === "undefined") {
  //     console.warn("Recording is not available in this environment.");
  //     return;
  //   }

  //   if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
  //     console.warn("getUserMedia is not supported in this browser.");
  //     return;
  //   }

  //   const AudioContextCtor = (window as any).AudioContext || (window as any).webkitAudioContext;
  //   if (!AudioContextCtor) {
  //     console.warn("WebAudio AudioContext is not available in this browser.");
  //     return;
  //   }

  //   try {
  //     addLog("Starting hands-free WebAudio session (VAD)...");

  //     // Clean previous URLs
  //     if (recordedAudioUrl) {
  //       URL.revokeObjectURL(recordedAudioUrl);
  //       setRecordedAudioUrl(null);
  //     }
  //     if (recordedWavUrl) {
  //       URL.revokeObjectURL(recordedWavUrl);
  //       setRecordedWavUrl(null);
  //     }
  //     recordedWavBlobRef.current = null;
  //     setAudioLogs([]);

  //     const stream = await navigator.mediaDevices.getUserMedia({
  //       audio: {
  //         echoCancellation: true,
  //         noiseSuppression: true,
  //         autoGainControl: true,
  //       },
  //     });
  //     mediaStreamRef.current = stream;

  //     const audioContext: AudioContext = new AudioContextCtor();
  //     audioContextRef.current = audioContext;
  //     webAudioSampleRateRef.current = audioContext.sampleRate;

  //     const source = audioContext.createMediaStreamSource(stream);
  //     sourceNodeRef.current = source;

  //     const bufferSize = 4096;
  //     const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
  //     scriptProcessorRef.current = processor;

  //     webAudioPcmDataRef.current = [];
  //     webAudioStatsRef.current = {
  //       processCount: 0,
  //       nonZeroSampleCount: 0,
  //       maxSampleSeen: 0,
  //     };
  //     vadSpeechActiveRef.current = false;
  //     vadSilenceMsRef.current = 0;
  //     vadSegmentMsRef.current = 0;

  //     // VAD parameters (tuned conservatively)
  //     const silenceThreshold = 0.01; // amplitude threshold
  //     const minSegmentMs = 400; // minimal speech duration to consider a question
  //     const silenceToEndMs = 2000; // 2 seconds required trailing silence to close the segment
  //     const bufferDurationMs = (bufferSize / audioContext.sampleRate) * 1000;

  //     processor.onaudioprocess = (event: AudioProcessingEvent) => {
  //       if (!isChatSessionActiveRef.current) {
  //         return;
  //       }

  //       const input = event.inputBuffer.getChannelData(0);
  //       const samplesCopy = new Float32Array(input.length);
  //       samplesCopy.set(input);

  //       webAudioPcmDataRef.current.push(samplesCopy);

  //       let nonZero = 0;
  //       let maxAbs = 0;
  //       let sumSquares = 0;
  //       for (let i = 0; i < samplesCopy.length; i++) {
  //         const v = samplesCopy[i];
  //         if (v !== 0) {
  //           nonZero++;
  //         }
  //         const av = Math.abs(v);
  //         if (av > maxAbs) {
  //           maxAbs = av;
  //         }
  //         sumSquares += v * v;
  //       }

  //       const stats = webAudioStatsRef.current;
  //       stats.processCount += 1;
  //       stats.nonZeroSampleCount += nonZero;
  //       if (maxAbs > stats.maxSampleSeen) {
  //         stats.maxSampleSeen = maxAbs;
  //       }

  //       const rms = Math.sqrt(sumSquares / (samplesCopy.length || 1));

  //       // Simple RMS-based VAD
  //       if (rms > silenceThreshold) {
  //         vadSpeechActiveRef.current = true;
  //         vadSilenceMsRef.current = 0;
  //         vadSegmentMsRef.current += bufferDurationMs;
  //       } else {
  //         if (vadSpeechActiveRef.current) {
  //           vadSilenceMsRef.current += bufferDurationMs;
  //           vadSegmentMsRef.current += bufferDurationMs;

  //           if (vadSilenceMsRef.current >= silenceToEndMs && vadSegmentMsRef.current >= minSegmentMs) {
  //             // We detected end of a speech segment (question). Send it as one turn.
  //             void sendCurrentVadSegment();
  //           }
  //         }
  //       }
  //     };

  //     source.connect(processor);
  //     processor.connect(audioContext.destination);

  //     setIsRecording(true);
  //     setIsChatSessionActive(true);
  //     isChatSessionActiveRef.current = true;

  //     addLog(`Hands-free WebAudio session started. sampleRate=${audioContext.sampleRate}, bufferSize=${bufferSize}`);
  //   } catch (error) {
  //     addLog(`Error starting hands-free WebAudio session: ${error instanceof Error ? error.message : String(error)}`, "error");
  //   }
  // };

  // Start user voice recording when clicking orange play button
  const handlePlayAudio = async (force: boolean = false) => {
    // If we are already recording, ignore extra clicks
    if (isRecording) {
      return;
    }

    // Do not start a new recording if the mic is logically disabled by the user, unless forced
    if (!force && !isMicEnabled) {
      addLog("Mic is disabled by user; recording will not start.");
      return;
    }

    // Basic feature detection for browsers (desktop + mobile web)
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      console.warn("Recording is not available in this environment.");
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn("getUserMedia is not supported in this browser.");
      return;
    }

    // Prefer WebAudio hands-free VAD session when available
    const AudioContextCtor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (AudioContextCtor) {
      try {
        await startHandsFreeWebAudioSession();
        setIsMicEnabled(true); // Only show orange aura when permission granted and session started
        setMicPermissionDeniedHint(false);
      } catch (err) {
        // User denied mic or other error: do not enable mic or show aura; allow retry on next click
        setIsChatSessionActive(false);
        isChatSessionActiveRef.current = false;
        isMicEnabledRef.current = false;
        const isPermissionDenied =
          err instanceof Error &&
          (err.name === "NotAllowedError" || err.message?.toLowerCase().includes("permission denied"));
        if (isPermissionDenied) {
          setMicPermissionDeniedHint(true);
          setMicDeniedSnackbarOpen(true); // Gemini-style: show snackbar for 2s, then auto-close
          addLog("Microphone permission denied. Allow it in your browser site settings (lock icon in the address bar) and click the mic again to retry.", "warn");
        }
      }
      return;
    }

    // Fallback: legacy single-turn MediaRecorder flow (kept intact)
    const MediaRecorderCtor = (window as any).MediaRecorder || (window as any).webkitMediaRecorder || (window as any).mozMediaRecorder || (window as any).msMediaRecorder;
    if (!MediaRecorderCtor) {
      console.warn("MediaRecorder API is not available in this browser.");
      return;
    }

    try {
      addLog("Requesting microphone access (MediaRecorder fallback)...");
      // Clean up previous recorded audio URLs if they exist
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
        setRecordedAudioUrl(null);
      }
      if (recordedWavUrl) {
        URL.revokeObjectURL(recordedWavUrl);
        setRecordedWavUrl(null);
      }

      recordedWavBlobRef.current = null;
      setAudioLogs([]);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true, // this is for removing the echo from the microphone
          noiseSuppression: true, // this is for removing the noise from the microphone
          autoGainControl: true, // this is for adjusting the gain of the microphone
          channelCount: 1, // mono instead of stereo (for reconozing human speech better)
          sampleRate: { ideal: 16000 }, // 16000 Hz is the ideal sample rate for human speech
        },
      });
      mediaStreamRef.current = stream;

      recordedChunksRef.current = [];
      const recorder: MediaRecorder = new MediaRecorderCtor(stream);

      recorder.ondataavailable = (event: any) => {
        const chunkInfo = `chunkType: ${event.data?.type || "unknown"}, recorderMimeType: ${recorder.mimeType || "unknown"}, size: ${event.data?.size || 0} bytes`;
        addLog(`Recorder data available: ${chunkInfo}`);
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstart = () => {
        addLog("Recording started (MediaRecorder fallback)");
        setIsRecording(true);
        setIsMicEnabled(true); // Permission granted and recording started; show orange aura
      };

      recorder.onstop = async () => {
        addLog("Recording stopped (MediaRecorder fallback)");
        setIsRecording(false);

        try {
          const firstChunkType = (recordedChunksRef.current[0] as any)?.type || recorder.mimeType || "";
          addLog(`First chunk type: ${firstChunkType || "empty"}, recorder.mimeType: ${recorder.mimeType || "empty"}`);
          const recordedBlob = firstChunkType && firstChunkType.length > 0 ? new Blob(recordedChunksRef.current, { type: firstChunkType }) : new Blob(recordedChunksRef.current);
          addLog(`Recorded audio blob (original): type=${recordedBlob.type || "unknown"}, size=${recordedBlob.size} bytes, chunks=${recordedChunksRef.current.length}`);

          if (recordedAudioUrl) {
            URL.revokeObjectURL(recordedAudioUrl);
          }
          if (recordedWavUrl) {
            URL.revokeObjectURL(recordedWavUrl);
          }

          const audioUrl = URL.createObjectURL(recordedBlob);
          setRecordedAudioUrl(audioUrl);
          addLog(`Recorded audio URL created (original format): ${audioUrl.substring(0, 50)}..., type=${recordedBlob.type || "unknown"}`);

          try {
            const wavBlob = await convertWebMToWav(recordedBlob);
            addLog(`Converted audio blob (WAV): size=${wavBlob.size} bytes`);

            const wavUrl = URL.createObjectURL(wavBlob);
            setRecordedWavUrl(wavUrl);
            addLog(`Recorded audio URL created (WAV): ${wavUrl.substring(0, 50)}...`);

            // Store the WAV blob in a ref so we can use it when sending to backend
            recordedWavBlobRef.current = wavBlob;

            // Automatically send the recorded WAV to backend and start streaming
            addLog("Sending recorded audio to backend (MediaRecorder fallback)...");
            await sendAudioToBackendAndStream(wavBlob);
          } catch (conversionError) {
            addLog(`Error converting recorded audio to WAV: ${conversionError instanceof Error ? conversionError.message : String(conversionError)}`, "error");
            recordedWavBlobRef.current = null;
          }
        } catch (err) {
          addLog(`Error while building recorded audio blob: ${err instanceof Error ? err.message : String(err)}`, "error");
        }

        // Always stop tracks to release microphone
        mediaStreamRef.current?.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (e) {
            console.warn("Error stopping media track", e);
          }
        });
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        recordedChunksRef.current = [];
        // Note: recordedWavBlobRef.current is kept so we can send it to backend later
      };

      recorder.onerror = (event) => {
        addLog(`MediaRecorder error: ${event instanceof Error ? event.message : String(event)}`, "error");
        setIsRecording(false);
        // Try to clean up stream
        mediaStreamRef.current?.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (e) {
            addLog(`Error stopping media track after error: ${e instanceof Error ? e.message : String(e)}`, "warn");
          }
        });
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        recordedChunksRef.current = [];
        recordedWavBlobRef.current = null;
        // Clean up WAV URL if it exists
        if (recordedWavUrl) {
          URL.revokeObjectURL(recordedWavUrl);
          setRecordedWavUrl(null);
        }
      };

      mediaRecorderRef.current = recorder;
      // Use a small timeslice to force browsers like Safari iOS to emit non-empty data chunks
      recorder.start(1000);
      addLog(`MediaRecorder started with mimeType: ${recorder.mimeType || "default"} (timeslice=1000ms)`);
    } catch (error) {
      addLog(`Error starting audio recording: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  };

  // Stop user voice recording when clicking orange stop button
  const handleStopAudio = () => {
    addLog("Stop audio button clicked");
    setIsChatSessionActive(false);
    onClose();
    isChatSessionActiveRef.current = false;

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        addLog(`Stopping MediaRecorder, current state: ${recorder.state}`);
        recorder.stop();
        return;
      } catch (e) {
        addLog(`Error stopping MediaRecorder: ${e instanceof Error ? e.message : String(e)}`, "error");
      }
    }

    if (audioContextRef.current) {
      stopWebAudioRecording();
      return;
    }

    addLog("No active recorder found");
    setIsRecording(false);
  };

  // Cleanup on unmount to ensure media resources are released
  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {
        console.warn("Error stopping recorder on unmount", e);
      }
      if (audioContextRef.current) {
        stopWebAudioRecording();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (e) {
            console.warn("Error stopping media track on unmount", e);
          }
        });
      }
      // Clean up blob URLs on unmount
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
      }
      if (recordedWavUrl) {
        URL.revokeObjectURL(recordedWavUrl);
      }
      if (chatPcmPlayerRef.current) {
        chatPcmPlayerRef.current.stop();
        chatPcmPlayerRef.current = null;
      }
    };
  }, [recordedAudioUrl, recordedWavUrl]);

  // Helper: convert WAV Blob to base64 string
  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(base64 || "");
      };
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(blob);
    });
  };

  // Function to send audio WAV to backend over persistent WebSocket.
  const sendAudioToBackendAndStream = async (audioWavBlob: Blob) => {
    if (checkCreditGate()) return; // if credits below REACT_APP_CHATBOT_CREDITS, open bench/paywall (chatbot drawer stays open)
    if (!isChatSocketReady || !chatSocketRef.current) {
      addLog("Chatbot WebSocket not ready. Skipping WS send.", "warn");
      return;
    }

    try {
      addLog(`Sending audio to backend over WS (WAV): size=${audioWavBlob.size} bytes`);
      cancelledResponseRef.current = false;

      // Reset current turn's user transcript ref so input_transcript_chunk accumulates for this turn only
      currentUserTranscriptRef.current = "";
      // Reset current transcript before starting new stream (but keep history)
      setCurrentChatTranscript("");
      setHasLiveChatAudio(false);

      // Clean up any existing audio player before starting new stream
      if (chatPcmPlayerRef.current) {
        chatPcmPlayerRef.current.stop();
        chatPcmPlayerRef.current = null;
      }

      const base64Audio = await blobToBase64(audioWavBlob);
      if (!base64Audio || base64Audio.length === 0) {
        addLog("blobToBase64 returned empty string", "warn");
        return;
      }

      // Build league context metadata so backend can fetch common_data and super_analysis from cache.
      let platformUserId: string | null = null;
      let leagueContextId: string | null = null;
      let leagueIntegration: Record<string, unknown> | null = null;

      if (selectedTeamInfo) {
        const platformId = selectedTeamInfo?.platformId;
        const currentUserDataPlatform = userDataCache?.[platformId];
        const integrationName: string | undefined = currentUserDataPlatform?.integration_name;

        leagueContextId = leagueIdPlatform || selectedTeamInfo.leagueId || null;

        if (integrationName) {
          // Mirror backend getPlatformUserId logic as closely as possible.
          const normalizedIntegration = integrationName.toLowerCase();
          const isYahoo = normalizedIntegration === "yahoo";
          const isMfl = normalizedIntegration === "mfl";
          const isEspn = normalizedIntegration === "espn";

          // League token used in other endpoints (e.g. super_analysis_streaming).
          let leagueToken: string | null | undefined = currentUserDataPlatform?.token;
          if (isYahoo) {
            leagueToken = yahooToken || leagueToken;
          }

          if (isYahoo || isMfl || isEspn) {
            platformUserId = leagueToken || null;
          } else {
            platformUserId = currentUserDataPlatform?.user_id || null;
          }
        }

        // league_integration for POC (same format as waivers), sent only when opening POC connection
        if (currentUserDataPlatform && pocToken && leagueContextId) {
          leagueIntegration = {
            // pocToken,
            token: currentUserDataPlatform.integration_name === "yahoo" ? yahooToken : currentUserDataPlatform.token, // before it was "leagueToken" field, now it is "token" field to match with back/poc body
            user_id: currentUserDataPlatform.user_id,
            league_id: leagueContextId,
            integration_name: resolveUnsyncedPocIntegrationName(
              currentUserDataPlatform.integration_name,
              platformId,
            ),
          };
          if (currentUserDataPlatform.integration_name === "espn") {
            (leagueIntegration as Record<string, unknown>).swid = currentUserDataPlatform.swid;
            (leagueIntegration as Record<string, unknown>).espn_s2 = currentUserDataPlatform.s2;
          }
        }
      }

      const message = {
        type: "user_audio",
        payload: base64Audio,
        mimeType: "audio/wav",
        authId,
        platformUserId,
        leagueId: leagueContextId,
        ...(leagueIntegration && { league_integration: leagueIntegration }),
      };
      console.log("message authId", authId);
      console.log("message platformUserId", platformUserId);
      console.log("message leagueId", leagueContextId);

      chatSocketRef.current.send(JSON.stringify(message));

      registerPendingTurn(currentUserTranscriptRef.current);
      // Activate loader - waiting for response; clear previous thought so we show "thinking" until first thought
      setCurrentThoughtTitle("");
      setIsWaitingForResponse(true);
      setCurrentTurnScrollTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error sending audio over chatbot WebSocket", error);
      addLog(`Error sending audio over chatbot WebSocket: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  };

  // Function to send plain text question to backend over persistent WebSocket.
  const sendTextToBackendAndStream = async (text: string) => {
    // For normal user sends we respect isChatSocketReady
    if (!isChatSocketReady || !chatSocketRef.current) {
      addLog("Chatbot WebSocket not ready. Skipping text send.", "warn");
      return;
    }

    try {
      addLog(`Sending text to backend over WS: length=${text.length}`);
      cancelledResponseRef.current = false;

      // Show user's text immediately in YOU and keep for history on complete (ref avoids state timing on complete)
      currentUserTranscriptRef.current = text;
      setCurrentUserTranscript(text);

      // Reset assistant transcript for new turn (history will be updated from WS events)
      setCurrentChatTranscript("");
      setHasLiveChatAudio(false);

      // Clean up any existing audio player before starting new turn
      if (chatPcmPlayerRef.current) {
        chatPcmPlayerRef.current.stop();
        chatPcmPlayerRef.current = null;
      }

      // Build league context metadata so backend can fetch common_data and super_analysis from cache.
      let platformUserId: string | null = null;
      let leagueContextId: string | null = null;
      let leagueIntegration: Record<string, unknown> | null = null;

      if (selectedTeamInfo) {
        const platformId = selectedTeamInfo?.platformId;
        const currentUserDataPlatform = userDataCache?.[platformId];
        const integrationName: string | undefined = currentUserDataPlatform?.integration_name;

        leagueContextId = leagueIdPlatform || selectedTeamInfo.leagueId || null;

        if (integrationName) {
          const normalizedIntegration = integrationName.toLowerCase();
          const isYahoo = normalizedIntegration === "yahoo";
          const isMfl = normalizedIntegration === "mfl";
          const isEspn = normalizedIntegration === "espn";

          let leagueToken: string | null | undefined = currentUserDataPlatform?.token;
          if (isYahoo) {
            leagueToken = yahooToken || leagueToken;
          }

          if (isYahoo || isMfl || isEspn) {
            platformUserId = leagueToken || null;
          } else {
            platformUserId = currentUserDataPlatform?.user_id || null;
          }
        }

        // league_integration for POC (same format as waivers), sent only when opening POC connection
        if (currentUserDataPlatform && pocToken && leagueContextId) {
          leagueIntegration = {
            // pocToken,
            token: currentUserDataPlatform.integration_name === "yahoo" ? yahooToken : currentUserDataPlatform.token, // before it was "leagueToken" field, now it is "token" field to match with back/poc body
            user_id: currentUserDataPlatform.user_id,
            league_id: leagueContextId,
            integration_name: resolveUnsyncedPocIntegrationName(
              currentUserDataPlatform.integration_name,
              platformId,
            ),
          };
          if (currentUserDataPlatform.integration_name === "espn") {
            (leagueIntegration as Record<string, unknown>).swid = currentUserDataPlatform.swid;
            (leagueIntegration as Record<string, unknown>).espn_s2 = currentUserDataPlatform.s2;
          }
        }
      }

      const message = {
        type: "user_text",
        payload: text,
        authId,
        platformUserId,
        leagueId: leagueContextId,
        ...(leagueIntegration && { league_integration: leagueIntegration }),
      };

      console.log("text message user_text", message.payload);
      // console.log("text message platformUserId", platformUserId);
      // console.log("text message leagueId", leagueContextId);

      chatSocketRef.current.send(JSON.stringify(message));

      void trackChatbotMessageSent(
        authId ?? "unknown",
        selectedTeamInfo?.platformId ?? "unknown",
        (leagueIdPlatform || selectedTeamInfo?.leagueId) ?? "unknown",
        selectedTeamInfo?.teamName ?? "unknown",
      );

      // Reset grounding sources for the new turn
      currentTurnSourcesRef.current = [];

      registerPendingTurn(text);
      // Activate loader - waiting for response; clear previous thought so we show "thinking" until first thought
      setCurrentThoughtTitle("");
      setIsWaitingForResponse(true);
      setCurrentTurnScrollTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error sending text over chatbot WebSocket", error);
      addLog(`Error sending text over chatbot WebSocket: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  };
  sendTextToBackendAndStreamRef.current = sendTextToBackendAndStream;

  // Function to play the audio: send test WAV to backend (simulating recorded audio)
  const handlePlayAudioTest = async () => {
    try {
      // Cargar el WAV de prueba desde assets como Blob (simula el audio grabado del usuario)
      const response = await fetch(testAudio);
      const audioBlob = await response.blob();
      console.log("audioBlob test", audioBlob);

      await sendAudioToBackendAndStream(audioBlob);
    } catch (error) {
      console.error("Error loading test audio", error);
      addLog(`Error loading test audio: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  };

  const isMobile = isMobileWebBrowser() || mobile;
  const isInputEnabled = isChatSocketReady;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        pb: 3,
        backgroundColor: "black",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none", // Firefox
        "&::-webkit-scrollbar": {
          display: "none", // Chrome, Safari, Edge
        },
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header container */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: "black",
        }}
      >
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1} pb={1}>
          <IconButton sx={{ visibility: "hidden" }} aria-label="back" onClick={handleDrawerClose}>
            <ArrowBackIcon sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }} />
          </IconButton>
          <Typography variant="h4" color={PureLightThemeCaaS.colors.alpha.white[100]} textAlign="center" className={styles.tradeTeam}>
            Chatbot for {selectedPartner}
          </Typography>
          <IconButton aria-label="close" onClick={handleDrawerClose}>
            <CloseIcon sx={{ color: PureLightThemeCaaS.colors.alpha.white[100] }} />
          </IconButton>
        </Box>
      </Box>

      <ChatMessagesViewport
        visible={messagesViewportVisible}
        isDrawerOpen={isDrawerOpen}
        isLoadingHistory={isLoadingHistory}
        isLoadingMoreHistory={isLoadingMoreHistory}
        hasMoreHistory={hasMoreHistory}
        onLoadMoreHistory={loadMoreHistory}
        chatHistory={chatHistory}
        isWaitingForResponse={isWaitingForResponse}
        currentChatTranscript={currentChatTranscript}
        currentUserTranscript={currentUserTranscript}
        currentThoughtTitle={currentThoughtTitle}
        chatTypewriterSpeed={chatTypewriterSpeed}
        currentTurnScrollTrigger={currentTurnScrollTrigger}
        authId={authId}
        platformId={chatTeamContext.platformId}
        leagueId={chatTeamContext.leagueId}
        teamName={chatTeamContext.teamName}
        onShareMessage={handleShareMessage}
        isCheckmark={isCheckmark}
      />

      <Box
        sx={{
          width: "100%",
          bgcolor: "#000000",
          mt: "auto", // This pushes it to the bottom
          mb: 1,
          flexShrink: 0, // Prevents it from shrinking
        }}
      >
        {/* ----------------------- */}
        {/* Audio logs: AudioWorklet/ScriptProcessor + any ERROR so failures are visible when testing (e.g. iPhone SE) */}
        {/* {(() => {
          const bugRelevantLogs = audioLogs.filter(
            (line) =>
              line.includes("AudioWorklet") ||
              line.includes("ScriptProcessor") ||
              line.toLowerCase().includes("error")
          );
          return bugRelevantLogs.length > 0 ? (
            <Box sx={{ bgcolor: `#222222`, width: "95%", margin: "0 auto", borderRadius: 2.5, py: 0.5, px: 1, mb: 0.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: PureLightThemeCaaS.colors.alpha.white[50] }}>
                  Audio capture / errors
                </Typography>

              </Box>
              <Box
                sx={{
                  maxHeight: 100,
                  overflowY: "auto",
                  fontSize: 11,
                  fontFamily: "monospace",
                  color: PureLightThemeCaaS.colors.alpha.white[70],
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.2)", borderRadius: 2 },
                }}
              >
                {bugRelevantLogs.map((line, i) => (
                  <Typography key={i} component="div" sx={{ fontSize: 11, fontFamily: "monospace", wordBreak: "break-word" }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : null;
        })()} */}
        {/* ----------------------- */}
        {/* Source and credits box */}
        {/* Same 95% width as textfield below so credits align with textfield on desktop and mobile */}
        <Box sx={{ width: "95%", margin: "0 auto", p: 0 }}>
          <Box sx={{ position: "relative", minHeight: 40 }}>
            {/* Credits: absolute so they don't take space in flow; left: 0 aligns with textfield */}
            <Box sx={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", zIndex: 2, p: 1, pl: 1.5 }}>
              {isLoggedInAuth0ExperienceGlobal && userSubscription !== "free" ? (
                <CreditsUi credits={credits} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef} />
              ) : (
                <FreeCreditsUi freeCredits={freeCredits} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef} />
              )}
            </Box>
            {/* SourceIcon full width so accordion opens in the middle; icon stays right */}
            <Box sx={{ width: "100%" }}>
              <SourceIcon customPaddingBottom={0.5} customPaddingTop={0.5} />
            </Box>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="stretch"
          minHeight="56px"
          pt={1}
          pb={1}
          gap={0}
          sx={{ bgcolor: `#222222`, width: "95%", margin: "0 auto", borderRadius: 2, pt: 2, px: 0, pb: 0 }}
        >
          <ChatBotQuestionInput
              ref={questionInputRef}
              disabled={!isInputEnabled}
              hasActiveTurn={hasActiveTurn}
              isMicEnabled={isMicEnabled}
              isRecording={isRecording}
              isUserSpeaking={isUserSpeaking}
              micPermissionDeniedHint={micPermissionDeniedHint}
              maxLength={CHATBOT_INPUT_MAX_LENGTH}
              queuedMessages={queuedMessages}
              onSend={handleSendText}
              onCancelResponse={handleCancelResponse}
              onRemoveQueuedAt={handleRemoveQueuedAt}
              onToggleMic={handleToggleMic}
              onStopMicFromTyping={handleStopMicFromTyping}
              onStableTextChange={handleStableTextChange}
          />
        </Box>
        {/* <SourceStories customPaddingBottom={0.5} customPaddingTop={0.5} /> */}

      </Box>


      {/* Gemini-style snackbar when mic permission denied: bottom, black bg, white text, white border, no shadow, X next to text */}
      <Snackbar
        open={micDeniedSnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setMicDeniedSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 0.4 }}>
            <Typography component="span" sx={{ color: "#fff" }}>
              The microphone cannot be accessed. Check the settings.
            </Typography>
            <IconButton size="small" color="inherit" onClick={() => setMicDeniedSnackbarOpen(false)} aria-label="Dismiss" sx={{ p: 0.25, ml: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        ContentProps={{
          sx: {
            mb: 2,
            mx: 1.5,
            backgroundColor: "#000",
            color: "#fff",
            border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[70]}`,
            boxShadow: "none",
            "& .MuiSnackbarContent-message": { flex: 1, padding: 0 },
          },
        }}
        sx={{ zIndex: 1400 }}
      />
    </Box>
  );
};

export default ChatBotResponseStream;
